import { CommonModule } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, EventEmitter, HostListener, inject, Input, Output } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';

export type FilterKey =
  | 'type'
  | 'physician'
  | 'facility'
  | 'surgery'
  | 'clinicExam'
  | 'questionnaire'
  | 'adHoc'
  | 'status';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterGroup {
  key: FilterKey;
  label: string;
  options: FilterOption[];
  multi?: boolean; // defaults to true
}

@Component({
  selector: 'spri-search-text',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
  ],
  templateUrl: './search-text.component.html',
  styleUrls: ['./search-text.component.scss'],
})
export class SpriSearchTextComponent implements OnInit {
  @Input() public placeholder = '';
  @Input() public value = '';

  /** Pass your own filter config or use defaults below */
  @Input() public filters: FilterGroup[] = [];

  /** Emits when the user clicks SEARCH or presses Enter in the main input */
  @Output() public searchClicked = new EventEmitter<{ text: string; selected: Record<string, string[]> }>();
  /** Emits when the user clicks RESET */
  @Output() public resetClicked = new EventEmitter<void>();

  public showFilterPanel = false;
  public selectedFilterType: FilterKey | null = null;
  public hoveredKey: FilterKey | null = null;

  /** Selected values per group */
  public selected: Record<FilterKey, string[]> = {
    type: [],
    physician: [],
    facility: [],
    surgery: [],
    clinicExam: [],
    questionnaire: [],
    adHoc: [],
    status: [],
  };

  /** Per-menu filter text */
  public menuSearchText: Record<FilterKey, string> = {
    type: '',
    physician: '',
    facility: '',
    surgery: '',
    clinicExam: '',
    questionnaire: '',
    adHoc: '',
    status: '',
  };

  public form!: FormGroup;
  private fb = inject(FormBuilder);

  public ngOnInit(): void {
    this.form = this.fb.group({
      query: [this.value ?? ''],
    });
  }

  /** Open/close main panel */
  public toggleFilterPanel(): void {
    this.showFilterPanel = true;
  }

  /** Close on ESC */
  @HostListener('document:keydown.escape')
  public onEsc(): void {
    if (this.showFilterPanel) {
      this.showFilterPanel = false;
    }
  }

  /** Menu open/close for chips */
  public onFilterButtonClick(filterType: FilterKey): void {
    this.selectedFilterType = this.selectedFilterType === filterType ? null : filterType;
  }

  public clearMenuSearch(key: FilterKey): void {
    this.menuSearchText[key] = '';
  }

  public isChecked(key: FilterKey, val: string): boolean {
    return this.selected[key]?.includes(val);
  }

  public toggleOption(key: FilterKey, val: string): void {
    // Ensure the bucket exists to avoid runtime errors when an unexpected key is passed
    const arr = (this.selected[key] ??= []);
    const i = arr.indexOf(val);
    if (i >= 0) {
      arr.splice(i, 1);
    } else {
      arr.push(val);
    }
    // trigger change detection
    this.selected = { ...this.selected };
  }

  public countFor(key: FilterKey): number {
    return this.selected[key]?.length ?? 0;
  }

  public chipLabel(group: FilterGroup): string {
    const count = this.countFor(group.key);
    return count ? `${group.label} (${count})` : group.label;
  }

  /** Enter in main input triggers search */
  public onMainKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Enter') {
      this.onSearch();
    }
  }

  public onSearch(): void {
    const text = (this.form.value?.query ?? '').trim();
    this.searchClicked.emit({ text, selected: this.selected });
    // Close the panel after an explicit search (Enter or button)
    this.closePanel();
  }

  public onReset(): void {
    Object.keys(this.selected).forEach((k) => (this.selected[k as FilterKey] = []));
    // preserve current text unless user edits it; emit reset with cleared chips
    this.selectedFilterType = null;
    this.resetClicked.emit();
    // Close the panel after reset
    this.closePanel();
  }

  /** Utility for filtering options inside a menu */
  public filteredOptions(group: FilterGroup): FilterOption[] {
    const q = (this.menuSearchText[group.key] || '').toLowerCase();
    if (!q) {
      return group.options;
    }
    return group.options.filter((o) => o.label.toLowerCase().includes(q));
  }

  public clearGroup(key: FilterKey, ev: MouseEvent): void {
    ev.stopPropagation();
    ev.preventDefault();
    this.selected[key] = [];
    this.selected = { ...this.selected };
  }

  public onChipEnter(key: FilterKey): void {
    this.hoveredKey = key;
  }
  public onChipLeave(): void {
    this.hoveredKey = null;
  }

  // add these methods to the component
  public openPanel(): void {
    this.showFilterPanel = true;
    document.body.classList.add('spri-modal-open');
  }

  public closePanel(): void {
    this.showFilterPanel = false;
    document.body.classList.remove('spri-modal-open');
  }
}
